/**
 * BucketSelect (PHASE1 §7.4). A dropdown over the canonical bucket list (read
 * from the shared `['buckets']` cache), grouped visually by icon+name. Value is
 * the numeric bucket id (or `null` when `allowClear`).
 */
import { useMemo } from 'react';
import { Select, type SelectOption } from './Select.tsx';
import { useBuckets } from '../../lib/hooks/useBuckets.ts';

export interface BucketSelectProps {
  value: number | null;
  onChange: (bucketId: number | null) => void;
  allowClear?: boolean;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
}

const CLEAR_VALUE = '';

export function BucketSelect({
  value,
  onChange,
  allowClear,
  disabled,
  id,
  placeholder = 'Kategorie wählen …',
}: BucketSelectProps) {
  const buckets = useBuckets();

  const options = useMemo<SelectOption[]>(() => {
    const list: SelectOption[] = buckets.map((bucket) => ({
      value: String(bucket.id),
      label: bucket.icon ? `${bucket.icon} ${bucket.name}` : bucket.name,
    }));
    if (allowClear) {
      return [{ value: CLEAR_VALUE, label: 'Keine Kategorie' }, ...list];
    }
    return list;
  }, [buckets, allowClear]);

  return (
    <Select
      id={id}
      disabled={disabled}
      placeholder={placeholder}
      value={value === null ? (allowClear ? CLEAR_VALUE : undefined) : String(value)}
      onValueChange={(next) => onChange(next === CLEAR_VALUE ? null : Number(next))}
      options={options}
    />
  );
}
